import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Landing2Layout from './layout/Landing2Layout';
import Sym from './components/Sym';
import strapiAPI from '../../utils/api';

/**
 * Registro con el look del rediseño (2 pasos) + la lógica REAL del alta:
 *   POST /api/public/register → auto-login (strapi_jwt_token + user_data) →
 *   /app/account/plans/upgrade?from=register (ahí se elige plan y se captura la
 *   tarjeta en el checkout de Stripe; el tier inicial trae los 15 días de prueba).
 *
 * Decisiones de negocio aplicadas (23-jul):
 *  - CON tarjeta al inicio → fuera el chip/nota «Sin tarjeta»; la nota correcta es
 *    «el pago se captura al contratar tu plan».
 *  - Sin testimonios ficticios (§4.7).
 *  - No hay pantalla ficticia de «prueba activa»: el alta lleva al checkout real.
 */
const inputStyle = {
  width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 12,
  padding: '14px 16px', color: 'var(--heading)', font: "500 1rem 'Hanken Grotesk'", outline: 'none',
};
const labelStyle = { font: "600 .9rem 'Hanken Grotesk'", color: 'var(--body-strong)', marginBottom: 8, display: 'block' };

const CHIPS = [
  { icon: 'check_circle', t: '15 días de prueba' },
  { icon: 'lock', t: 'e.firma cifrada' },
  { icon: 'event_repeat', t: 'Cancela cuando quieras' },
];

const FLOW = [
  { icon: 'cloud', title: 'El SAT', sub: 'Tus recibos ya timbrados', accent: false },
  { icon: 'receipt_long', title: 'PaySlip', sub: 'Descarga y organiza todo', accent: true },
  { icon: 'groups', title: 'Tu equipo', sub: 'Cada quien recibe el suyo', accent: false },
];

const Field = ({ label, hint, children }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {children}
    {hint && <div style={{ font: "400 .82rem 'Hanken Grotesk'", color: 'var(--faint)', marginTop: 7 }}>{hint}</div>}
  </div>
);

const Register2 = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ company_name: '', rfc: '', first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const next = () => {
    if (!form.company_name.trim() || !form.rfc.trim()) {
      setError('Escribe el nombre y el RFC de tu empresa.');
      return;
    }
    setError(null);
    setStep(2);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.first_name.trim() || !form.email.trim()) {
      setError('Escribe tu nombre y tu correo.');
      return;
    }
    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await strapiAPI.post('/api/public/register', {
        company_name: form.company_name.trim(),
        rfc: form.rfc.trim().toUpperCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
      });
      const { jwt, user } = response?.data || {};
      if (!jwt || !user) throw new Error('Respuesta inesperada del servidor');

      // Sesión con el MISMO shape que authService.login (role 'company').
      const userData = {
        id: user.id, documentId: user.documentId, username: user.username, email: user.email,
        user_type: user.user_type, role: 'company', first_name: user.first_name, last_name: user.last_name,
        is_active: user.is_active, must_change_password: user.must_change_password,
        password_changed_at: null, profile: user, loginTime: new Date().toISOString(),
      };
      localStorage.setItem('strapi_jwt_token', jwt);
      localStorage.setItem('user_data', JSON.stringify(userData));

      // Directo a elegir plan → checkout de Stripe (el pago se captura ahí).
      navigate('/app/account/plans/upgrade?from=register', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || 'No pudimos crear tu cuenta. Inténtalo de nuevo.';
      setError(msg);
      setBusy(false);
    }
  };

  return (
    <Landing2Layout title="Prueba 15 días — PaySlip">
      {/* Hero */}
      <section style={{ position: 'relative', overflow: 'clip', padding: 'clamp(40px,6vw,72px) 0 clamp(24px,4vw,40px)' }}>
        <div aria-hidden style={{ position: 'absolute', top: -120, right: -80, width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle,rgba(47,107,255,.22),transparent 65%)', filter: 'blur(18px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto', padding: '0 clamp(20px,5vw,40px)', textAlign: 'center' }}>
          <span style={{ font: "700 .8rem 'IBM Plex Mono'", letterSpacing: '.14em', textTransform: 'uppercase', color: '#6FA0FF' }}>Prueba 15 días</span>
          <h1 style={{ font: "600 clamp(2.4rem,5.5vw,3.9rem)/1.03 'Source Serif 4'", letterSpacing: '-.02em', color: 'var(--heading)', margin: '14px 0 0', textWrap: 'balance' }}>Crea tu cuenta y prueba PaySlip</h1>
          <p style={{ font: "400 clamp(1.05rem,1.6vw,1.22rem)/1.6 'Hanken Grotesk'", color: 'var(--body)', margin: '18px auto 0', maxWidth: 600 }}>En dos minutos tienes tu empresa dada de alta. El plan inicial incluye 15 días de prueba; el pago se captura al contratar tu plan.</p>
        </div>
      </section>

      <section style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(16px,3vw,28px) clamp(20px,5vw,40px) clamp(56px,8vw,104px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 28, alignItems: 'start' }}>
          {/* Panel de valor */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <h2 style={{ font: "600 clamp(1.55rem,3vw,2.15rem)/1.14 'Source Serif 4'", color: 'var(--heading)', margin: '0 0 10px', textWrap: 'balance' }}>
                Estrena PaySlip con los recibos de <span style={{ fontStyle: 'italic', color: '#6FA0FF' }}>tu propia empresa</span>
              </h2>
              <p style={{ font: "400 1.02rem/1.55 'Hanken Grotesk'", color: 'var(--body)', margin: 0 }}>Conecta tu e.firma y, en minutos, PaySlip baja del SAT tus recibos ya timbrados y se los entrega a tu equipo. Esto es lo que pasa:</p>
            </div>

            <div data-dark style={{ position: 'relative', overflow: 'hidden', background: '#0A1326', border: '1px solid rgba(255,255,255,.07)', borderRadius: 18, padding: '22px 20px' }}>
              <div style={{ font: "700 .72rem 'IBM Plex Mono'", letterSpacing: '.12em', textTransform: 'uppercase', color: '#6FA0FF', marginBottom: 16 }}>Así funciona</div>
              {FLOW.map((n, i) => (
                <React.Fragment key={n.title}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: n.accent ? '#2F6BFF' : 'rgba(111,160,255,.14)', border: n.accent ? 'none' : '1px solid rgba(111,160,255,.3)', display: 'grid', placeItems: 'center', flexShrink: 0, boxShadow: n.accent ? '0 8px 20px -6px rgba(47,107,255,.8)' : 'none' }}>
                      <Sym name={n.icon} size={24} color={n.accent ? '#fff' : '#6FA0FF'} />
                    </div>
                    <div>
                      <div style={{ font: "700 1rem 'Hanken Grotesk'", color: '#F4F7FC' }}>{n.title}</div>
                      <div style={{ font: "400 .86rem 'Hanken Grotesk'", color: '#8A97B2' }}>{n.sub}</div>
                    </div>
                  </div>
                  {i < FLOW.length - 1 && <div style={{ width: 3, height: 22, marginLeft: 20, background: 'repeating-linear-gradient(180deg,#2F6BFF 0 5px,transparent 5px 11px)', backgroundSize: '3px 11px', opacity: 0.5 }} />}
                </React.Fragment>
              ))}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CHIPS.map((c) => (
                <span key={c.t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--well-row)', border: '1px solid var(--border-soft)', borderRadius: 999, padding: '8px 13px', font: "600 .8rem 'IBM Plex Mono'", color: 'var(--body-strong)' }}>
                  <Sym name={c.icon} size={16} color="#6FA0FF" />{c.t}
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '16px 18px' }}>
              <span style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(47,107,255,.14)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <Sym name="credit_card" size={22} color="#6FA0FF" />
              </span>
              <p style={{ font: "500 .96rem/1.45 'Hanken Grotesk'", color: 'var(--body-strong)', margin: 0 }}>No se te cobra al registrarte. El pago se captura al contratar tu plan.</p>
            </div>

            <p style={{ font: "400 .9rem/1.5 'Hanken Grotesk'", color: 'var(--muted-2)', margin: '0 4px' }}>
              ¿Ya tienes cuenta? <a onClick={() => navigate('/login')} style={{ fontWeight: 700, color: '#6FA0FF', cursor: 'pointer' }}>Inicia sesión</a> · ¿Más de 5,000 colaboradores? <a onClick={() => navigate('/contact')} style={{ fontWeight: 700, color: '#6FA0FF', cursor: 'pointer' }}>Habla con ventas</a>
            </p>
          </div>

          {/* Formulario 2 pasos */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 22, padding: 'clamp(24px,3.5vw,40px)' }}>
            <div style={{ marginBottom: 26 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span style={{ font: "700 .74rem 'IBM Plex Mono'", letterSpacing: '.12em', textTransform: 'uppercase', color: '#6FA0FF' }}>Paso {step} de 2</span>
                <span style={{ font: "600 .8rem 'IBM Plex Mono'", color: 'var(--muted-2)' }}>15 días de prueba</span>
              </div>
              <div style={{ height: 6, borderRadius: 999, background: 'var(--track)', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg,#6FA0FF,#2F6BFF)', borderRadius: 999, width: step === 1 ? '50%' : '100%', transition: 'width .45s ease' }} />
              </div>
              <h2 style={{ font: "600 clamp(1.5rem,3vw,2rem) 'Source Serif 4'", color: 'var(--heading)', margin: '16px 0 0' }}>{step === 1 ? 'Datos de tu empresa' : 'Crea tu acceso'}</h2>
            </div>

            {error && (
              <div style={{ font: "500 .9rem 'Hanken Grotesk'", color: '#F87171', background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 18 }}>{error}</div>
            )}

            {step === 1 ? (
              <div style={{ display: 'grid', gap: 18 }}>
                <Field label="Nombre o razón social de tu empresa">
                  <input value={form.company_name} onChange={set('company_name')} placeholder="Mi Empresa S.A. de C.V." style={inputStyle} />
                </Field>
                <Field label="RFC de la empresa" hint="Lo usamos con tu e.firma solo para consultar el SAT.">
                  <input value={form.rfc} onChange={set('rfc')} placeholder="ABC123456XY9" style={{ ...inputStyle, textTransform: 'uppercase' }} />
                </Field>
                <button type="button" onClick={next}
                  style={{ width: '100%', padding: 16, font: "700 1rem 'IBM Plex Mono'", color: '#fff', background: '#2F6BFF', border: 'none', borderRadius: 13, cursor: 'pointer', boxShadow: '0 12px 30px -12px rgba(47,107,255,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
                  Continuar<Sym name="arrow_forward" size={20} />
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit}>
                <div style={{ display: 'grid', gap: 18 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <Field label="Nombre"><input value={form.first_name} onChange={set('first_name')} placeholder="Tu nombre" style={inputStyle} /></Field>
                    <Field label="Apellidos"><input value={form.last_name} onChange={set('last_name')} placeholder="Tus apellidos" style={inputStyle} /></Field>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <Field label="Correo (será tu usuario)"><input type="email" value={form.email} onChange={set('email')} placeholder="tu@empresa.mx" style={inputStyle} /></Field>
                    <Field label="Teléfono (opcional)"><input value={form.phone} onChange={set('phone')} placeholder="55 0000 0000" style={inputStyle} /></Field>
                  </div>
                  <Field label="Contraseña"><input type="password" value={form.password} onChange={set('password')} placeholder="Mínimo 8 caracteres" style={inputStyle} autoComplete="new-password" /></Field>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
                    <button type="button" onClick={() => { setError(null); setStep(1); }}
                      style={{ padding: '16px 20px', font: "700 1rem 'IBM Plex Mono'", color: 'var(--heading)', background: 'transparent', border: '1.5px solid var(--border-strong)', borderRadius: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                      <Sym name="arrow_back" size={20} />Atrás
                    </button>
                    <button type="submit" disabled={busy}
                      style={{ flex: 1, padding: 16, font: "700 1rem 'IBM Plex Mono'", color: '#fff', background: '#2F6BFF', border: 'none', borderRadius: 13, cursor: busy ? 'default' : 'pointer', boxShadow: '0 12px 30px -12px rgba(47,107,255,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
                      {busy
                        ? <><span style={{ width: 19, height: 19, border: '2.5px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'ls2-spin .7s linear infinite' }} />Creando…</>
                        : <>Crear cuenta y elegir plan<Sym name="arrow_forward" size={20} /></>}
                    </button>
                  </div>
                  <p style={{ font: "400 .85rem/1.5 'Hanken Grotesk'", color: 'var(--faint)', margin: 0, textAlign: 'center' }}>
                    Al crear tu cuenta aceptas los Términos y el Aviso de privacidad de PaySlip. No se te cobra al registrarte; el pago se captura al contratar tu plan.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </Landing2Layout>
  );
};

export default Register2;
